import { DataTableColumn } from './fromAgColDefs';

const getLeaves = (columns: DataTableColumn[]): DataTableColumn[] =>
  columns.flatMap((c) => (c.columns ? getLeaves(c.columns) : [c]));

// The columns an export writes: the ones shown, plus the hidden change
// columns a matrix shows inside its values' cells (changeFields), as AG's
// export is given them
const getExportedIds = (columns: DataTableColumn[]) => {
  const leaves = getLeaves(columns);
  const changeFields = new Set(
    leaves
      .filter((leaf) => !leaf.meta.isHidden)
      .flatMap(
        (leaf) =>
          (leaf.meta.cellRendererParams.changeFields as string[] | undefined) ??
          [],
      ),
  );
  return new Set(
    leaves
      .filter((leaf) => !leaf.meta.isHidden || changeFields.has(leaf.id))
      .map((leaf) => leaf.id),
  );
};

// The column tree with only the exported leaves (and groups that keep any)
const pruneColumns = ({
  columns,
  exportedIds,
}: {
  columns: DataTableColumn[];
  exportedIds: Set<string>;
}): DataTableColumn[] =>
  columns.flatMap((c) => {
    if (!c.columns) {
      return exportedIds.has(c.id) ? [c] : [];
    }
    const children = pruneColumns({ columns: c.columns, exportedIds });
    return children.length > 0 ? [{ ...c, columns: children }] : [];
  });

const getDepth = (columns: DataTableColumn[]): number =>
  Math.max(...columns.map((c) => (c.columns ? 1 + getDepth(c.columns) : 1)));

// One label per leaf column for each header level above the leaves. A group
// writes its name over its first leaf and leaves the rest blank, as AG's
// column-group header row does.
const buildGroupHeaderRows = (columns: DataTableColumn[]): string[][] => {
  const depth = getDepth(columns);
  const rows: string[][] = [];
  for (let level = 0; level < depth - 1; level++) {
    const row: string[] = [];
    const visit = (list: DataTableColumn[], currentLevel: number) =>
      list.forEach((c) => {
        const leafCount = c.columns ? getLeaves(c.columns).length : 1;
        if (currentLevel === level) {
          const label = c.columns ? c.header : '';
          row.push(label, ...Array(leafCount - 1).fill(''));
        } else if (c.columns) {
          visit(c.columns, currentLevel + 1);
        } else {
          row.push('');
        }
      });
    visit(columns, 0);
    rows.push(row);
  }
  return rows;
};

const toCellString = (value: unknown) =>
  value === undefined || value === null ? '' : `${value}`;

const toExportColumn = (leaf: DataTableColumn): DataTable.ExportColumn => ({
  getColId: () => leaf.id,
  getColDef: () => leaf.meta.colDef,
  getUserProvidedColDef: () => leaf.meta.userColDef,
});

// The header labels and every row's processed cell values, in the order the
// table shows them. Both the live table export and the headless CSV builder
// end here, so they can't drift.
const buildExportCells = ({
  columns,
  rows,
  totals,
  processCell,
  processHeader,
  columnIds,
}: {
  columns: DataTableColumn[];
  rows: DataTable.Row[];
  totals: DataTable.Row[];
  processCell: DataTable.ProcessExportCell;
  processHeader?: DataTable.ProcessExportHeader;
  // Only these of the exported columns (AG's columnKeys)
  columnIds?: string[];
}): DataTable.ExportCells => {
  const exportedIds = getExportedIds(columns);
  const exported = pruneColumns({
    columns,
    exportedIds: columnIds
      ? new Set(columnIds.filter((id) => exportedIds.has(id)))
      : exportedIds,
  });
  const leaves = getLeaves(exported);
  const toRow = ({
    data,
    rowIndex,
    isPinned,
  }: {
    data: DataTable.Row;
    rowIndex: number;
    isPinned: boolean;
  }) =>
    leaves.map((leaf) => {
      const value = leaf.accessorFn ? leaf.accessorFn(data, rowIndex) : '';
      return toCellString(
        processCell({
          value,
          column: toExportColumn(leaf),
          node: { data, rowPinned: isPinned ? 'bottom' : null },
        }),
      );
    });

  return {
    headerRows: [
      ...buildGroupHeaderRows(exported),
      leaves.map((leaf) =>
        processHeader
          ? toCellString(processHeader({ column: toExportColumn(leaf), rows }))
          : leaf.header,
      ),
    ],
    rows: [
      ...rows.map((data, rowIndex) =>
        toRow({ data, rowIndex, isPinned: false }),
      ),
      ...totals.map((data, rowIndex) =>
        toRow({ data, rowIndex, isPinned: true }),
      ),
    ],
  };
};

const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;

// The same layout AG's getDataAsCsv writes: every value quoted, CRLF lines
export const toCsv = ({ headerRows, rows }: DataTable.ExportCells) =>
  [...headerRows, ...rows].map((row) => row.map(quote).join(',')).join('\r\n');

export default buildExportCells;
